/**
 * [Phase 2d Stripe - Session B, founder-locked redlines]
 *
 * useEntitlements - single source of truth for the three canonical
 * access toggles on the frontend:
 *
 *   • hasAuctionAccess     - can create + join auction drafts
 *   • hasDraftAnalytics    - can see PGF Analytics tab during a draft
 *   • hasSeasonAnalytics   - can see PGF Analytics in-season
 *
 * Toggles come from GET /api/stripe/entitlements. Admin users +
 * `alpha_flags.pgf_analytics_alpha` invited testers bypass (the
 * backend stamps `has_*` true in the response).
 *
 * The hook also exposes:
 *   • auctionLeaguesUsed, auctionLeagueCap, auctionExtended  (gate maths)
 *   • refresh()  - re-fetch (used after a successful upgrade)
 *
 * NOTE: callers MUST handle the loading state. Defaults are all FALSE
 * so unpaid surfaces render their paywall on the first paint.
 */
import { useCallback, useEffect, useState } from "react";
import { authHeaders, useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  loading: true,
  hasAuctionAccess: false,
  hasDraftAnalytics: false,
  hasSeasonAnalytics: false,
  auctionLeaguesUsed: 0,
  auctionLeagueCap: 10,
  auctionExtended: false,
  entitlements: [],
};

export function useEntitlements() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isAlphaTester = !!user?.alpha_flags?.pgf_analytics_alpha;
  const [state, setState] = useState(EMPTY);

  const refresh = useCallback(async () => {
    if (!user) {
      setState({ ...EMPTY, loading: false });
      return;
    }
    try {
      const r = await fetch(`${API}/stripe/entitlements`, {
        headers: authHeaders(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setState({
        loading: false,
        hasAuctionAccess: !!d.has_auction_access || isAdmin,
        // Alpha testers retain analytics access during the rollout.
        hasDraftAnalytics: !!d.has_draft_analytics || isAdmin || isAlphaTester,
        hasSeasonAnalytics: !!d.has_season_analytics || isAdmin || isAlphaTester,
        auctionLeaguesUsed: d.auction_leagues_used || 0,
        auctionLeagueCap: d.auction_league_cap ?? null,
        auctionExtended: !!d.auction_extended,
        entitlements: d.entitlements || [],
      });
    } catch (e) {
      // Fail-closed for paid surfaces; bypass for admin/alpha.
      setState({
        loading: false,
        hasAuctionAccess: isAdmin,
        hasDraftAnalytics: isAdmin || isAlphaTester,
        hasSeasonAnalytics: isAdmin || isAlphaTester,
        auctionLeaguesUsed: 0,
        auctionLeagueCap: 10,
        auctionExtended: false,
        entitlements: [],
      });
    }
  }, [user, isAdmin, isAlphaTester]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { ...state, refresh };
}

export default useEntitlements;
